import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import {
  getAllStudentService,
  getAllStudentV2Service,
  createStudentService,
  updateStudentService,
  deleteStudentService,
  getStudentService,
} from "@/service/student.service";
import { StudentModel, StudentQuery } from "@/types/student.model";
import type { AxiosError } from "axios";
import { ROLES, successToast } from "@/utils/constants";
import { fetchDashboardData } from "./dashboardSlice";

// Define the state interface
interface StudentState {
  students: StudentModel[];
  currentStudent: StudentModel | null;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  totalItems: number;
  noStudents: boolean;
  query: StudentQuery;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

// Initial state
const initialState: StudentState = {
  students: [],
  currentStudent: null,
  loading: false,
  actionLoading: false,
  error: null,
  totalItems: 0,
  noStudents: true,
  query: {
    page: 1,
    pageSize: 10,
    search: "",
    fromDate: undefined,
    toDate: undefined,
    sortBy: undefined,
    schoolId: undefined,
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
  },
};

// Async thunk for fetching students with pagination and filtering
export const fetchStudents = createAsyncThunk(
  "student/fetchAll",
  async (query: StudentQuery, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

      const state = getState() as RootState;

      const { school_id } = state.auth.user || {};

      if (school_id) {
        query.schoolId = school_id;
      }

      const listRequest = query.type
        ? getAllStudentV2Service({
            ...query,
            type: query.type,
          })
        : getAllStudentService(query);

      const {
        data: { data, pageInfo },
      } = await listRequest;
      dispatch(setLoading(false));
      dispatch(setQuery(query));
      dispatch(setNoStudents(false));
      return {
        students: data,
        // Handle different API response formats for pagination
        pagination: {
          totalItems: pageInfo?.total_count as number,
          totalPages: pageInfo?.total_pages as number,
          currentPage: pageInfo?.current_page as number,
          pageSize: pageInfo?.per_page as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(clearStudents());
        dispatch(setQuery(query));
        dispatch(setNoStudents(true));
        return {
          students: [],
          // Handle different API response formats for pagination
          pagination: {
            totalItems: 0,
            totalPages: 0,
            currentPage: 0,
            pageSize: 0,
          },
        };
      }

      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch students";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Async thunk for fetching a single student by ID
export const fetchStudentById = createAsyncThunk(
  "student/fetchById",
  async (id: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await getStudentService(id);
      dispatch(setLoading(false));
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch student";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Async thunk for creating a new student
export const createNewStudent = createAsyncThunk(
  "student/create",
  async (studentData: Partial<StudentModel>, { dispatch }) => {
    try {
      dispatch(setActionLoading(true));
      const response = await createStudentService(studentData as StudentModel);
      dispatch(setActionLoading(false));
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to create student";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Async thunk for updating an existing student
export const updateExistingStudent = createAsyncThunk(
  "student/update",
  async (
    { id, studentData }: { id: string; studentData: Partial<StudentModel> },
    { dispatch }
  ) => {
    try {
      dispatch(setActionLoading(true));
      // Ensure the student object has the id property
      const studentToUpdate = { ...studentData };
      const response = await updateStudentService(
        id,
        studentToUpdate as StudentModel
      );
      dispatch(setActionLoading(false));
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to update student";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Async thunk for deleting a student
export const deleteExistingStudent = createAsyncThunk(
  "student/delete",
  async (id: string, { dispatch, getState }) => {
    try {
      dispatch(setActionLoading(true));
      const {
        data: { message },
      } = await deleteStudentService(id);

      successToast(message || "Student deleted successfully", "");

      // Refresh the projects list after adding
      const state = getState() as RootState;
      const userRole = state.auth.user?.role;

      const isSuperAdmin = userRole?.code === ROLES.SUPER_ADMIN;

      if (isSuperAdmin) {
        dispatch(fetchDashboardData());
      }
      dispatch(
        fetchStudents({
          ...state.students.query,
          page: state.students.pagination.currentPage,
          pageSize: state.students.pagination.pageSize,
        })
      );

      dispatch(setActionLoading(false));
      return id;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to delete student";
      dispatch(setError(message));
      throw error;
    }
  }
);

// Create the slice
const studentSlice = createSlice({
  name: "student",
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<StudentQuery>) => {
      state.query = action.payload;
    },
    clearStudents: (state) => {
      state.students = [];
    },
    setNoStudents: (state, action: PayloadAction<boolean>) => {
      state.noStudents = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearCurrentStudent: (state) => {
      state.currentStudent = null;
    },
    setActionLoading: (state, action: PayloadAction<boolean>) => {
      state.actionLoading = action.payload;
    },
    resetAllState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch students
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.students = action.payload.students;
        state.pagination = action.payload.pagination;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch students";
      })

      // Fetch student by id
      .addCase(fetchStudentById.fulfilled, (state, action) => {
        state.currentStudent = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchStudentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch student";
      })

      // Create student
      .addCase(createNewStudent.fulfilled, (state) => {
        state.loading = false;
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(createNewStudent.rejected, (state, action) => {
        state.loading = false;
        state.actionLoading = false;
        state.error = action.error.message || "Failed to create student";
      })

      // Update student
      .addCase(updateExistingStudent.fulfilled, (state) => {
        state.loading = false;
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(updateExistingStudent.rejected, (state, action) => {
        state.loading = false;
        state.actionLoading = false;
        state.error = action.error.message || "Failed to update student";
      })

      // Delete student
      .addCase(deleteExistingStudent.fulfilled, (state, action) => {
        state.loading = false;
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(deleteExistingStudent.rejected, (state, action) => {
        state.loading = false;
        state.actionLoading = false;
        state.error = action.error.message || "Failed to delete student";
      });
  },
});

// Export actions
export const {
  setNoStudents,
  setLoading,
  setError,
  clearCurrentStudent,
  setActionLoading,
  resetAllState,
  clearStudents,
  setQuery,
} = studentSlice.actions;

// Export reducer
export default studentSlice.reducer;

// Selectors
export const selectStudents = (state: RootState) => state.students.students;
export const selectCurrentStudent = (state: RootState) =>
  state.students.currentStudent;
export const selectStudentLoading = (state: RootState) =>
  state.students.loading;
export const selectStudentError = (state: RootState) => state.students.error;
export const selectTotalItems = (state: RootState) =>
  state.students.pagination.totalItems;
export const selectStudentActionLoading = (state: RootState) =>
  state.students.actionLoading;
export const selectNoStudents = (state: RootState) => state.students.noStudents;
export const selectQuery = (state: RootState) => state.students.query;
